/* INCLUDE OPTIONAL — one switch, read wherever a sign-off is decided
 * (Ale ruling 2026-09-05, Tier-2).
 *
 * Policy demands two kinds of proof: the MANDATORY ones, which hold the
 * record short until they land, and the ones policy only RECOMMENDS. A
 * sign-off never blocks on the recommended kind, and that stays true —
 * the counts do not move, the verbs do not change. But an officer about
 * to sign should be able to SEE what is merely recommended and still
 * missing, so he can ask for it in the same breath instead of coming
 * back to it later.
 *
 * So: a chip, "include optional · n", OFF by default. ON, the optional
 * demands are listed. The chain-status block, the Evidence tab and the
 * sign-off card all read THIS state, so wherever the officer flips it
 * the other two already agree — a chip that only half the page obeys is
 * worse than no chip.
 *
 * The choice is per officer, not per fund: it says how he likes to read
 * evidence, so it survives a reload.
 */

import { useCallback, useSyncExternalStore } from 'react'

const KEY = 'nolos.include-optional'

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === 'on'
  } catch {
    return false
  }
}

let on = read()
const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

function subscribe(l: () => void): () => void {
  listeners.add(l)
  return () => {
    listeners.delete(l)
  }
}

export function includeOptional(): boolean {
  return on
}

export function setIncludeOptional(next: boolean): void {
  if (on === next) return
  on = next
  try {
    localStorage.setItem(KEY, next ? 'on' : 'off')
  } catch {
    /* no storage — the switch still holds for this session */
  }
  emit()
}

/** The switch and its setter. OFF by default, everywhere, always. */
export function useIncludeOptional(): [boolean, (next: boolean) => void] {
  const value = useSyncExternalStore(
    subscribe,
    includeOptional,
    // The server render has no officer and no storage: OFF.
    () => false
  )
  const set = useCallback((next: boolean) => setIncludeOptional(next), [])
  return [value, set]
}
